// RumbleSiegerDisplayPage.jsx — Siegeranzeige fuer den Rumble-Guertelmodus.
// Zweiter Bildschirm / Beamer — gesteuert via localStorage + BroadcastChannel.
//
// Gestaltung: styles/leinwand.css, dieselbe wie die uebrigen Leinwaende. Das
// gewaehlte Hintergrundbild bleibt das Besondere dieser Anzeige; Kopfband,
// Name, Verein und Fussleiste sehen aus wie ueberall sonst.
import React, { useState, useEffect, useCallback } from 'react';
import '../styles/leinwand.css';
import './RumbleSiegerDisplayPage.css';

const LS_KEY_STATE = 'tda-rumble-sieger-state';
const LS_KEY_BG    = 'tda-rumble-winner-bg';
const CHANNEL      = 'tda-rumble-sieger';

const BG_OPTIONS = [
  { id: 'kickbox', src: '/images/rumble-bg-kickbox.webp', label: 'Kickboxen' },
  { id: 'karate',  src: '/images/rumble-bg-karate.webp',  label: 'Kampfkünste' },
];

export default function RumbleSiegerDisplayPage() {
  const [winnerData,   setWinnerData]   = useState(null);
  const [animKey,      setAnimKey]      = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [bgId,         setBgId]         = useState(
    () => localStorage.getItem(LS_KEY_BG) || BG_OPTIONS[0].id
  );

  const currentBg = BG_OPTIONS.find(b => b.id === bgId) || BG_OPTIONS[0];

  // Overflow hidden
  useEffect(() => {
    const prev = {
      htmlOverflow: document.documentElement.style.overflow,
      bodyOverflow: document.body.style.overflow,
    };
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow             = 'hidden';
    return () => {
      document.documentElement.style.overflow = prev.htmlOverflow;
      document.body.style.overflow             = prev.bodyOverflow;
    };
  }, []);

  // Auto-Vollbild beim Laden
  useEffect(() => {
    const timer = setTimeout(() => {
      document.documentElement.requestFullscreen?.({ navigationUI: 'hide' }).catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // Fullscreen-Status tracken
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  // Chat-Widget und externe Elemente verstecken
  useEffect(() => {
    const hide = () => {
      document.body.querySelectorAll('body > *:not(#root)').forEach(el => {
        if (!['SCRIPT', 'STYLE', 'LINK', 'META'].includes(el.tagName)) {
          el.style.setProperty('display', 'none', 'important');
        }
      });
    };
    hide();
    const obs = new MutationObserver(hide);
    obs.observe(document.body, { childList: true });
    return () => obs.disconnect();
  }, []);

  // Sieger-Daten empfangen (localStorage + BroadcastChannel)
  useEffect(() => {
    const applyState = (raw) => {
      try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (parsed?.type === 'show') {
          setWinnerData(parsed);
          setAnimKey(k => k + 1);
        } else {
          setWinnerData(null);
        }
      } catch (e) {}
    };

    // Sofort lesen beim Öffnen
    applyState(localStorage.getItem(LS_KEY_STATE));

    const onStorage = (e) => {
      if (e.key === LS_KEY_STATE) applyState(e.newValue);
    };
    window.addEventListener('storage', onStorage);

    let ch = null;
    try {
      ch = new BroadcastChannel(CHANNEL);
      ch.onmessage = (e) => applyState(e.data);
    } catch (e) {}

    return () => {
      window.removeEventListener('storage', onStorage);
      if (ch) ch.close();
    };
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else {
      document.documentElement.requestFullscreen?.({ navigationUI: 'hide' }).catch(() => {});
    }
  }, []);


  const selectBg = useCallback((id) => {
    setBgId(id);
    localStorage.setItem(LS_KEY_BG, id);
  }, []);

  // Setup-Screen solange kein Vollbild
  if (!isFullscreen) {
    return (
      <SetupOverlay
        bgOptions={BG_OPTIONS}
        selectedBg={bgId}
        onSelectBg={selectBg}
        onFullscreen={toggleFullscreen}
        currentBgSrc={currentBg.src}
      />
    );
  }

  return (
    <div
      className="lw rsd"
      style={{ backgroundImage: `url(${currentBg.src})` }}
      onClick={toggleFullscreen}
    >
      <div className="lw-kopf">
        <div className="lw-kopf-links">
          <div className="lw-kopf-turnier">Tiger &amp; Dragon Association</div>
          <div className="lw-kopf-kat">Rumble · Gürtelmodus</div>
        </div>
        <div className="lw-kopf-titel">勝利 SIEGER</div>
        <div className={`lw-kopf-status${winnerData ? '' : ' wartet'}`}>
          {/* Nur den besonderen Grund zeigen -- "SIEGER" steht schon in der Mitte. */}
          {winnerData
            ? (winnerData.winReason && winnerData.winReason.toUpperCase() !== 'SIEGER'
                ? winnerData.winReason
                : 'LIVE')
            : 'BEREIT'}
        </div>
      </div>

      <div className="lw-buehne">
        {winnerData
          ? <WinnerCard key={animKey} data={winnerData} />
          : <StandbyCard />}
      </div>

      <div className="lw-fuss">
        <div className="lw-fuss-lbl">Division</div>
        <div className="lw-fuss-wert">{winnerData?.divisionCode || '—'}</div>
        {/* Hintergrundbild-Wechsler — bleibt erreichbar, ohne das Bild zu stoeren */}
        <div className="rsd-bg-switcher" onClick={e => e.stopPropagation()}>
          {BG_OPTIONS.map(bg => (
            <button
              key={bg.id}
              className={`rsd-bg-dot${bgId === bg.id ? ' active' : ''}`}
              title={bg.label}
              aria-label={`Hintergrund ${bg.label}`}
              onClick={() => selectBg(bg.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Setup Overlay ─────────────────────────────────────────────────────
function SetupOverlay({ bgOptions, selectedBg, onSelectBg, onFullscreen, currentBgSrc }) {
  return (
    <div
      className="rsd-setup"
      style={{ backgroundImage: `url(${currentBgSrc})` }}
    >
      <div className="rsd-setup-card">
        <div className="rsd-setup-title">Rumble-Siegerleinwand</div>
        <div className="rsd-setup-sub">Hintergrundbild wählen, dann Vollbild starten</div>

        <div className="rsd-bg-grid">
          {bgOptions.map(bg => (
            <div
              key={bg.id}
              className={`rsd-bg-option${selectedBg === bg.id ? ' active' : ''}`}
              style={{ backgroundImage: `url(${bg.src})` }}
              onClick={() => onSelectBg(bg.id)}
              title={bg.label}
            >
              <div className="rsd-bg-option-label">{bg.label}</div>
            </div>
          ))}
        </div>

        <button className="rsd-fullscreen-btn" onClick={onFullscreen}>
          ⛶ Vollbild starten
        </button>
      </div>
    </div>
  );
}

// ── Standby ───────────────────────────────────────────────────────────
function StandbyCard() {
  return (
    <div className="rsd-karte">
      <div className="lw-kanji">武</div>
      <div className="lw-bereit-text">BEREIT</div>
    </div>
  );
}

// ── Winner Card ───────────────────────────────────────────────────────
function WinnerCard({ data }) {
  const { vorname, nachname, verein, belts = 3 } = data;

  return (
    <div className="rsd-karte">
      <div className="lw-name gross">{vorname} {nachname}</div>
      {verein && <div className="lw-unterzeile"><span className="lw-verein">{verein}</span></div>}

      {/* Gewonnene Guertel als Balken -- frueher standen hier Emoji, die je
          nach Rechner und Beamer anders aussahen. */}
      <div className="rsd-guertel" aria-label={`${belts} Gürtel`}>
        {Array.from({ length: Math.max(0, belts) }).map((_, i) => (
          <span key={i} className="rsd-guertel-balken" style={{ animationDelay: `${0.1 + i * 0.12}s` }} />
        ))}
      </div>
      <div className="rsd-guertel-zahl">{belts} × Gürtel</div>
    </div>
  );
}
